// Bevétel-előrejelzés — szolgáltatás-részletes drilldown + a NRR/PRR/függőben szem-ikon modálok (a legacy
// getServiceRevenueDetails / getNRRDetails / getPRRDetails / openPendingModal 1:1). Minden összeg a BFF-től jött
// Decimal-string; a formázást a hívó `fmt`-je végzi. A totál display-célú Number-összeg (a kártya-értékek az
// authoritatív forrás). A modál a közös `Dialog` (rögzített header/footer, görgő body).

import { EyeIcon } from '@heroicons/react/24/outline';
import { type TFunction } from 'i18next';
import { Fragment } from 'react';

import { detailTotal, ownerLine, type DetailScope } from './detail-utils';

import { badgeVisual, DomainBadge } from '@/components/shared/domain-badge';
import { ScopeBadge } from '@/components/shared/scope-badge';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Divider } from '@/components/ui/divider';
import { Text } from '@/components/ui/text';
import { formatMonthLabel } from '@/lib/month';
import { distinctPlanName } from '@/lib/service-plan-label';

type Formatter = (value: string) => string;
type Scope = DetailScope;

// A HATÓKÖR- és GYAKORISÁG-jelvények a KÖZÖS badge-katalógusból jönnek (`ScopeBadge` / `DomainBadge`), UGYANAZZAL
// a jellel és feliratkészlettel, amit a szolgáltatás-hozzárendelés lista mutat („ügyfél szintű" / „fiók szintű"
// aktatáska-/épület-ikonnal · „havi" kék körkörös nyíllal). Korábban itt saját, eltérő badge-ek éltek (violet/
// info tónus, 10 px-es betű, ikon nélkül) — ugyanaz a fogalom kétféle jelet kapott (user-lelet).

/** Hatókör-jelvény a drilldown-sorokon — a hozzárendelés-lista feliratával. */
function ScopeMark({ scope, t }: { scope: Scope; t: TFunction }): JSX.Element {
  return (
    <ScopeBadge
      scope={scope}
      label={t(`serviceAssignments.scopeLabel.${scope}`, { defaultValue: scope })}
    />
  );
}

/** Gyakoriság-jelvény (havi/negyedéves/…/eseti) — a detail-sorokon és a modálokban. */
function FreqMark({ frequency, t }: { frequency: string; t: TFunction }): JSX.Element {
  return (
    <DomainBadge
      kind="frequency"
      value={frequency}
      label={t(`revenueForecast.frequencyLabel.${frequency}`, { defaultValue: frequency })}
    />
  );
}

/** Az érték-badge-eknek átadott státusz-bontás összeg (a multi-státusz `*_by_status` badge-ekhez). */
export function StatusBreakdownBadges({
  breakdown,
  statuses,
  fmt,
  t,
}: {
  breakdown: { active: string; pending: string };
  /** A kiválasztott státuszok — CSAK multi-státusznál (>1) jelennek meg a badge-ek (legacy 1:1). */
  statuses: string[];
  fmt: Formatter;
  t: TFunction;
}): JSX.Element | null {
  if (statuses.length <= 1) return null;
  const items = (['active', 'pending'] as const).filter(
    (s) => statuses.includes(s) && Number(breakdown[s]) > 0,
  );
  if (items.length === 0) return null;
  return (
    <>
      {items.map((s) => {
        // a tónus ÉS az ikon az életciklus-kategóriából — eddig csak a tónus jött át, az ikon elmaradt
        const visual = badgeVisual('lifecycle', s);
        const Icon = visual?.icon;
        return (
          <Badge
            key={s}
            variant={visual?.variant}
            className="tabular-nums"
            icon={Icon ? <Icon /> : undefined}
          >
            {t(`revenueForecast.status.${s}`)} · {fmt(breakdown[s])}
          </Badge>
        );
      })}
    </>
  );
}

/** A részlet-modált nyitó „szem" ikon-gomb (a kártya jobb-felső sarkába, `action` slotba). */
export function EyeButton({ label, onClick }: { label: string; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded-full p-0.5 focus:outline-none focus-visible:ring-2"
    >
      <EyeIcon className="size-4" />
    </button>
  );
}

/** Egy normalizált detail-sor (NRR/PRR/függőben közös alakja). `frequency` null → nincs frekvencia-badge (NRR). */
export interface DetailRow {
  service: string;
  planName: string | null;
  scope: Scope;
  clientName: string;
  accountName: string;
  frequency: string | null;
  price: string;
}

/**
 * Közös részlet-modál: görgethető tétel-lista (szolgáltatás + plan, ügyfél/fiók + scope-badge, opcionális
 * frekvencia-badge, ár) + rögzített összesítő láb. Az NRR/PRR/függőben modál ezt használja, eltérő rows-szal.
 */
export function RevenueDetailDialog({
  open,
  onClose,
  title,
  description,
  rows,
  fmt,
  t,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Rövid magyarázat a cím alatt (pl. miért szerepelnek a tételek a listán). */
  description?: string | undefined;
  rows: DetailRow[];
  fmt: Formatter;
  t: TFunction;
}): JSX.Element {
  const total = detailTotal(rows);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[95vw] max-w-2xl sm:w-full">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <DialogBody className="p-0">
          {rows.length === 0 ? (
            <div className="text-muted-foreground p-6 text-center text-sm">
              {t('revenueForecast.details.empty')}
            </div>
          ) : (
            // A sorok közti vonal a KÖZÖS `Divider` (behúzva): korábban a `divide-y` a `<ul>`-on futott, így a
            // vonal a modál TELJES szélességét elfoglalta, a sorok viszont `px-6`-osak — a vonal „kilógott".
            <ul>
              {rows.map((row, i) => (
                <Fragment key={i}>
                  {/* A behúzás a SOROK paddingjével egy vonalban (`px-6`) — a `Divider` alap `mx-3`-a
                      általános, itt a tartalom-vonalhoz igazítjuk, hogy a vonal a szöveggel kezdődjön. */}
                  {i > 0 && <Divider spacing="none" className="mx-6" />}
                  <li className="flex items-start justify-between gap-3 px-6 py-3">
                    <div className="min-w-0">
                      <div className="text-foreground truncate text-sm font-medium">
                        {row.service}
                        {/* A csomag CSAK ha ELTÉR a szolgáltatás nevétől (`distinctPlanName`) — egyszerű
                            szerkezetnél a kettő azonos, és a sor önmagát ismételte. */}
                        {distinctPlanName(row.service, row.planName) !== null && (
                          <span className="text-muted-foreground font-normal">
                            {' '}
                            · {row.planName}
                          </span>
                        )}
                      </div>
                      <div className="text-muted-foreground mt-0.5 truncate text-xs">
                        {ownerLine(row)}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        <ScopeMark scope={row.scope} t={t} />
                        {row.frequency && <FreqMark frequency={row.frequency} t={t} />}
                      </div>
                    </div>
                    <span className="text-foreground shrink-0 text-sm font-semibold tabular-nums">
                      {fmt(row.price)}
                    </span>
                  </li>
                </Fragment>
              ))}
            </ul>
          )}
        </DialogBody>
        <DialogFooter className="flex items-center justify-between">
          <Text variant="secondary" as="span" className="text-sm">
            {t('revenueForecast.details.total')}
          </Text>
          <span className="text-foreground text-base font-semibold tabular-nums">{fmt(total)}</span>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Szolgáltatás-részletes drilldown (a byService chart alatt, ügyfél/fiók/szolgáltatás/alkategória szűrőnél) ──

interface DetailChild {
  name: string;
  price: string;
}
interface ServiceDetailItem {
  scope: Scope;
  status: string;
  clientName: string;
  accountName: string;
  mrr: string;
  isDue: boolean;
  periodPrice: string;
  nextBilledMonth: string | null;
  frequency: string;
  isOneTime: boolean;
  isComplex: boolean;
  children: DetailChild[];
}
interface ServiceDetailGroup {
  serviceName: string;
  totalMrr: string;
  assignments: ServiceDetailItem[];
}

/** A periodikus (negyedéves / féléves / éves) gyakoriságok — a kártyán a teljes díjjal, a díj fajtájával jelennek meg. */
const PERIODIC = new Set(['quarterly', 'semi_annual', 'annual']);

/** Egy hozzárendelés-sor a drilldown-ban (owner + scope/frekvencia/státusz badge + ár + children-bontás). */
function DrilldownItem({
  item,
  fmt,
  t,
  locale,
}: {
  item: ServiceDetailItem;
  fmt: Formatter;
  t: TFunction;
  locale: string;
}): JSX.Element {
  const owner = ownerLine(item);
  // PERIODIKUS tétel: a szám MINDIG a teljes díj (nem havi hányad), alatta kimondva, mire vonatkozik, és hogy
  // ebben a hónapban egyben számlázódik-e, vagy mikor lesz a következő számla (user-kérés: „éves, 100 000 Ft")
  const periodic = !item.isOneTime && PERIODIC.has(item.frequency);
  return (
    <div className="bg-sunken/60 rounded-lg px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="text-foreground block truncate text-xs font-medium">{owner}</span>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <ScopeMark scope={item.scope} t={t} />
            {item.isOneTime ? (
              // Eseti tétel: a gyakoriság-katalógus `one_time` jele (borostyán + nyugta) — ugyanaz, mint a
              // hozzárendelés-listán az „eseti" gyakoriság.
              <DomainBadge
                kind="frequency"
                value="one_time"
                label={t('revenueForecast.details.oneTime')}
              />
            ) : (
              <FreqMark frequency={item.frequency} t={t} />
            )}
            {item.status === 'pending' && (
              // Függő státusz: az életciklus-katalógus jele (borostyán + pause) — mint a hozzárendelés státusz-badge-e.
              <DomainBadge
                kind="lifecycle"
                value="pending"
                label={t('revenueForecast.status.pending')}
              />
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end">
          {periodic ? (
            <>
              <span
                className={
                  item.isDue
                    ? 'text-foreground text-xs font-semibold tabular-nums'
                    : 'text-muted-foreground text-xs font-semibold tabular-nums'
                }
              >
                {fmt(item.periodPrice)}
              </span>
            </>
          ) : (
            <>
              <span
                className={
                  item.isDue
                    ? 'text-foreground text-xs font-semibold tabular-nums'
                    : 'text-muted-foreground text-xs font-semibold tabular-nums'
                }
              >
                {fmt(item.mrr)}
              </span>
              {!item.isDue && (
                <span className="text-muted-foreground text-2xs mt-0.5">
                  {t('revenueForecast.details.notDue')}
                </span>
              )}
            </>
          )}
        </div>
      </div>
      {periodic && (
        // KÜLÖN SOR (nem a szám alatt a keskeny oszlopban): mobilon a hosszú felirat ott az ügyfél nevére csúszott
        <div className="text-muted-foreground text-2xs mt-1 text-right">
          {t(`revenueForecast.details.periodFee.${item.frequency}`)} ·{' '}
          {item.isDue
            ? t('revenueForecast.details.billedNow')
            : item.nextBilledMonth !== null
              ? t('revenueForecast.details.nextBilling', {
                  month: formatMonthLabel(item.nextBilledMonth, locale),
                })
              : t('revenueForecast.details.notDue')}
        </div>
      )}
      {item.isComplex && item.children.length > 0 && (
        <div className="border-border ml-4 mt-2 space-y-1 border-l-2 pl-3">
          {item.children.map((c, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground truncate">{c.name}</span>
              <span className="text-foreground/80 tabular-nums">{fmt(c.price)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Szolgáltatás-részletes drilldown: szolgáltatásonként kártya (fejléc + összeg) a hozzárendelés-sorokkal. */
/** A kiemelés hossza (ms) a csoportra ugráskor — elég, hogy a szem megtalálja, és nem marad ott zajként. */
const JUMP_HIGHLIGHT_MS = 1600;

/**
 * A grafikon egy sorára kattintva a részletező lista MEGFELELŐ csoportjához görget, és röviden kiemeli. A csoport
 * neve a grafikon sorának neve (a BFF ugyanabból a csomag-szintű bontásból építi mindkettőt). `false`, ha nincs ilyen
 * csoport (pl. a részletező nincs a lapon).
 */
export function scrollToServiceGroup(name: string): boolean {
  const el = [...document.querySelectorAll<HTMLElement>('[data-service-group]')].find(
    (e) => e.dataset.serviceGroup === name,
  );
  if (!el) return false;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  el.classList.add('ring-2', 'ring-brand');
  window.setTimeout(() => el.classList.remove('ring-2', 'ring-brand'), JUMP_HIGHLIGHT_MS);
  return true;
}

export function ServiceDrilldown({
  groups,
  fmt,
  t,
  locale,
}: {
  groups: ServiceDetailGroup[];
  fmt: Formatter;
  t: TFunction;
  locale: string;
}): JSX.Element | null {
  if (groups.length === 0) return null;
  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <div
          key={g.serviceName}
          data-service-group={g.serviceName}
          className="card-glass scroll-mt-4 rounded-xl p-5 transition-shadow"
        >
          <div className="mb-3 flex items-center justify-between gap-2">
            <Text variant="itemTitle" as="h3">
              {g.serviceName}
            </Text>
            <span className="text-muted-foreground text-xs">
              {t('revenueForecast.details.dueRevenue')} ·{' '}
              <span className="text-success text-sm font-bold tabular-nums">{fmt(g.totalMrr)}</span>
            </span>
          </div>
          <div className="space-y-2">
            {g.assignments.map((item, i) => (
              <DrilldownItem key={i} item={item} fmt={fmt} t={t} locale={locale} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
